
import { useState, useEffect } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, isBefore, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CustomDatePickerProps {
  selected?: Date;
  onSelect: (date: Date | undefined) => void;
  placeholder: string;
  className?: string;
  unavailableDates?: Date[];
  minDate?: Date;
  autoOpenNext?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  isAdminContext?: boolean; // Nueva prop para contexto de admin
}

const CustomDatePicker = ({ 
  selected, 
  onSelect, 
  placeholder, 
  className,
  unavailableDates = [],
  minDate,
  autoOpenNext,
  open: controlledOpen,
  onOpenChange,
  title,
  isAdminContext = false // Por defecto false para mantener comportamiento existente
}: CustomDatePickerProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Use controlled open state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  // Auto-open effect and sync month with selected date
  useEffect(() => {
    if (controlledOpen) {
      // If there's a selected date, show that month, otherwise show current month
      if (selected) {
        setCurrentMonth(selected);
      } else {
        setCurrentMonth(new Date());
      }
    }
  }, [controlledOpen, selected]);

  // Debug log to track unavailable dates
  useEffect(() => {
    if (unavailableDates.length > 0) {
      console.log('📅 CustomDatePicker unavailable dates:', unavailableDates.map(d => d.toDateString()));
    }
  }, [unavailableDates]);

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    
    // Check if date is unavailable
    const isUnavailable = unavailableDates.some(unavailableDate => {
      const normalizedUnavailable = startOfDay(unavailableDate);
      const normalizedSelected = startOfDay(date);
      const match = normalizedUnavailable.getTime() === normalizedSelected.getTime();
      if (match) {
        console.log(`❌ CustomDatePicker: Date ${date.toDateString()} is unavailable (matches ${unavailableDate.toDateString()})`);
      }
      return match;
    });

    // Check if date is before minDate (solo si no estamos en contexto de admin)
    let isBeforeMin = false;
    if (!isAdminContext) {
      const today = startOfDay(new Date());
      const effectiveMinDate = minDate ? startOfDay(minDate) : today;
      isBeforeMin = isBefore(startOfDay(date), effectiveMinDate);
    }

    if (isUnavailable || isBeforeMin) {
      console.log(`❌ CustomDatePicker: Rejecting date ${date.toDateString()} - unavailable: ${isUnavailable}, beforeMin: ${isBeforeMin}, adminContext: ${isAdminContext}`);
      return; // Don't select unavailable or invalid dates
    }

    console.log(`✅ CustomDatePicker: Selecting date ${date.toDateString()} (Admin: ${isAdminContext})`);
    onSelect(date);
    setOpen(false);
    
    // Trigger auto-open next picker if provided
    if (autoOpenNext) {
      setTimeout(autoOpenNext, 100);
    }
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const getAllDaysToShow = () => {
    const startOfWeek = getStartOfWeek();
    const days = [];
    
    // Generar 6 semanas (42 días) para cubrir todo el mes
    for (let i = 0; i < 42; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    
    return days;
  };

  const getStartOfWeek = () => {
    const start = startOfMonth(currentMonth);
    const startOfWeek = new Date(start);
    startOfWeek.setDate(start.getDate() - start.getDay());
    return startOfWeek;
  };

  const isDateUnavailable = (date: Date) => {
    const isUnavailable = unavailableDates.some(unavailableDate => {
      const normalizedUnavailable = startOfDay(unavailableDate);
      const normalizedDate = startOfDay(date);
      return normalizedUnavailable.getTime() === normalizedDate.getTime();
    });
    
    if (isUnavailable) {
      console.log(`❌ CustomDatePicker: Date ${date.toDateString()} marked as unavailable`);
    }
    
    return isUnavailable;
  };

  const isDateBeforeMin = (date: Date) => {
    // En contexto de admin, permitir cualquier fecha
    if (isAdminContext) return false;
    
    // Default to today if no minDate provided
    const today = startOfDay(new Date());
    const effectiveMinDate = minDate ? startOfDay(minDate) : today;
    return isBefore(startOfDay(date), effectiveMinDate);
  };

  const displayText = selected 
    ? format(selected, "dd/MM/yyyy") 
    : placeholder;

  const monthYear = format(currentMonth, "MMMM yyyy", { locale: es });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal h-12 bg-white border-gray-200",
            !selected && "text-gray-500",
            className
          )}
        >
          {displayText}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-50" align="start">
        <div className="p-4 bg-white rounded-lg shadow-lg w-80">
          {/* Título del picker */}
          {title && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700">{title}</h3>
              {isAdminContext && (
                <p className="text-xs text-blue-600 mt-1">Modo admin: fechas pasadas permitidas</p>
              )}
            </div>
          )}

          {/* Header con navegación */}
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={prevMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <h2 className="text-lg font-semibold text-gray-900 capitalize">
              {monthYear}
            </h2>
            
            <button 
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Días de la semana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendario */}
          <div className="grid grid-cols-7 gap-1">
            {getAllDaysToShow().map((day, index) => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isSelected = selected && isSameDay(day, selected);
              const isCurrentDay = isToday(day);
              const isUnavailable = isDateUnavailable(day);
              const isBeforeMin = isDateBeforeMin(day);
              const isDisabled = isUnavailable || isBeforeMin;
              
              return (
                <button
                  key={index}
                  onClick={() => handleDateSelect(day)}
                  disabled={isDisabled}
                  className={cn(
                    "w-10 h-10 text-sm font-medium rounded-lg transition-colors",
                    "hover:bg-gray-100",
                    !isCurrentMonth && "text-gray-300",
                    isCurrentMonth && "text-gray-900",
                    isSelected && "bg-gray-900 text-white hover:bg-gray-900",
                    isCurrentDay && !isSelected && "bg-gray-100 text-gray-900 font-semibold",
                    isDisabled && "text-gray-300 line-through cursor-not-allowed hover:bg-transparent opacity-50"
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CustomDatePicker;
